import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const AVATARS = [
  { initials: 'DR', bg: '#B2E0D4', color: '#0F6E56' },
  { initials: 'SN', bg: '#A5C8E1', color: '#2A5F7E' },
  { initials: 'TM', bg: '#E8B4C8', color: '#7E2A50' },
  { initials: 'KV', bg: '#E8D4A5', color: '#7E6A2A' },
]

function HomePage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', email: '', practice: '' })
  const [count, setCount] = useState(9)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [btnError, setBtnError] = useState(false)

  useEffect(() => {
    fetch('/api/waitlist/count')
      .then(r => r.json())
      .then(d => { if (d.count) setCount(d.count) })
      .catch(() => {})
  }, [])

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.practice) {
      setBtnError(true)
      setTimeout(() => setBtnError(false), 2000)
      return
    }

    setLoading(true)

    try {
      const [res] = await Promise.all([
        fetch('/api/waitlist/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_name: form.name.trim(),
            email: form.email.trim(),
            practice_type: form.practice,
            source: 'waitlist_page'
          })
        }),
        new Promise(r => setTimeout(r, 1200))
      ])

      if (!res.ok) throw new Error()

      setCount(prev => prev + 1)
      setSuccess(true)
    } catch {
      setLoading(false)
      setBtnError(true)
      setTimeout(() => setBtnError(false), 2000)
    }
  }

  const spotsLeft = Math.max(0, 285 - count)
  const firstName = form.name.trim().split(' ')[0]

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box', background: 'white'
  }

  return (
    <div className="public-page" style={{ maxWidth: 620, margin: '0 auto', padding: '0 1rem 3rem' }}>

      {/* Navbar */}
      <nav aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0', marginBottom: 16 }}>
        <img src="/clinicapt.svg" alt="Clinicapt - Online bookings and reviews for SA practices" style={{ height: 24 }} />
        <button
          onClick={() => navigate('/login')}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, color: '#888', fontFamily: "'DM Sans', sans-serif",
            padding: '6px 10px', borderRadius: 6,
            transition: 'color 0.15s'
          }}
          onMouseEnter={e => e.target.style.color = '#333'}
          onMouseLeave={e => e.target.style.color = '#888'}
        >
          Sign in
        </button>
      </nav>

      <main>
        {/* Hero Section */}
        <header style={{ marginBottom: 32 }}>
          {/* Early Access Badge */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              color: '#999',
              borderRadius: 100, padding: '6px 0',
              fontSize: 13, fontWeight: 400, letterSpacing: 0.2
            }}>
              <span className="pulse-dot" style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#1D9E75', display: 'inline-block',
                opacity: 0.7
              }} />
              Now accepting early access
            </div>
          </div>

          {/* Hero Headline */}
          <h1 className="waitlist-headline" style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 40, lineHeight: 1.15, fontWeight: 400,
            textAlign: 'center', marginBottom: 16, color: '#111',
            letterSpacing: 'normal'
          }}>
            Stop losing patients<br />
            to <em style={{ fontStyle: 'italic', color: '#1D9E75' }}>missed calls &amp; no-shows.</em>
          </h1>

          {/* Subheading */}
          <p style={{
            textAlign: 'center', fontSize: 16, color: '#3a3a3a',
            maxWidth: 520, margin: '0 auto 32px', lineHeight: 1.6
          }}>
            Clinicapt gives South African practices online booking, WhatsApp appointment
            reminders, and automated Google review collection in one platform.
          </p>

          {/* Trust Stat */}
          <div style={{ textAlign: 'center' }}>
            <p style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#E1F5EE', borderRadius: 100,
              padding: '8px 18px', fontSize: 14, color: '#0F6E56', margin: 0
            }}>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#1D9E75' }}>83%</span>
              of patients check Google reviews before choosing a practice
            </p>
          </div>
        </header>

        {/* Pain Points */}
        <section aria-label="Common practice challenges" style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Patients call during lunch, get voicemail, and book elsewhere',
              '30\u201340% of no-shows could\u2019ve been recovered with a WhatsApp reminder',
              'Competitors with more Google reviews are ranking above you on Maps'
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div aria-hidden="true" style={{
                  width: 22, height: 22, minWidth: 22, borderRadius: '50%',
                  background: '#FCEBEB', color: '#A32D2D',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, marginTop: 1
                }}>{'\u2715'}</div>
                <span style={{ fontSize: 14, color: '#3a3a3a', lineHeight: 1.5 }}>{text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Social Proof Counter Bar */}
        <section aria-label="Waitlist count" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f9f9f9', border: '0.5px solid #e0e0e0',
          borderRadius: 10, padding: '12px 14px', marginBottom: 24
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex' }} aria-hidden="true">
              {AVATARS.map((a, i) => (
                <div key={i} style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: a.bg, color: a.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                  border: '2px solid #f9f9f9',
                  marginLeft: i > 0 ? -8 : 0,
                  zIndex: 4 - i, position: 'relative'
                }}>{a.initials}</div>
              ))}
            </div>
            <span style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>
              <strong>{count}</strong> practices on the waitlist
            </span>
          </div>
          <span style={{
            background: '#FFF8E1', color: '#B8860B',
            borderRadius: 100, padding: '4px 10px',
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap'
          }}>{spotsLeft} spots left</span>
        </section>

        {/* Waitlist Form or Success State */}
        <section aria-label="Join the waitlist" style={{
          border: '0.5px solid #e0e0e0', borderRadius: 12,
          padding: 24, background: 'white', marginBottom: 32
        }}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#E1F5EE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <h2 style={{
                fontFamily: "'DM Serif Display', serif", fontSize: 24,
                fontWeight: 400, color: '#111', marginBottom: 8, letterSpacing: 'normal'
              }}>You're on the list!</h2>
              <p style={{ fontSize: 14, color: '#3a3a3a', lineHeight: 1.6 }}>
                We'll reach out when your access is ready. You've locked in founding-member
                pricing, thanks for joining early, {firstName}.
              </p>
            </div>
          ) : (
            <form onSubmit={e => { e.preventDefault(); handleSubmit() }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                <div>
                  <label htmlFor="waitlist-name" style={{ fontSize: 13, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Your name</label>
                  <input
                    id="waitlist-name"
                    className="waitlist-input"
                    type="text"
                    placeholder="Dr. Nomvula Dlamini"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label htmlFor="waitlist-email" style={{ fontSize: 13, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Work email</label>
                  <input
                    id="waitlist-email"
                    className="waitlist-input"
                    type="email"
                    placeholder="nomvula@sunrisedental.co.za"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label htmlFor="waitlist-practice" style={{ fontSize: 13, fontWeight: 500, color: '#333', display: 'block', marginBottom: 4 }}>Type of practice</label>
                  <select
                    id="waitlist-practice"
                    className="waitlist-input"
                    value={form.practice}
                    onChange={e => setForm({ ...form, practice: e.target.value })}
                    style={{ ...inputStyle, color: form.practice ? '#333' : '#999' }}
                  >
                    <option value="">Select your practice type</option>
                    <option value="General practitioner (GP)">General practitioner (GP)</option>
                    <option value="Dental practice">Dental practice</option>
                    <option value="Physiotherapy / Biokineticist">Physiotherapy / Biokineticist</option>
                    <option value="Specialist practice">Specialist practice</option>
                    <option value="Optometry">Optometry</option>
                    <option value="Psychology / Counselling">Psychology / Counselling</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <button
                className="waitlist-btn"
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', padding: '12px 20px',
                  background: btnError ? '#A32D2D' : '#1D9E75',
                  color: 'white', border: 'none', borderRadius: 8,
                  fontSize: 15, fontWeight: 600, cursor: 'pointer',
                  opacity: loading ? 0.7 : 1,
                  pointerEvents: loading ? 'none' : 'auto',
                  fontFamily: "'DM Sans', sans-serif",
                  transition: 'background 0.2s'
                }}
              >
                {btnError ? 'Please fill in all fields' : loading ? 'Saving your spot...' : 'Reserve my early access spot'}
              </button>

              <p style={{ fontSize: 12, color: '#777', textAlign: 'center', marginTop: 12 }}>
                No credit card needed. Launch pricing locked in for waitlist members.
              </p>
            </form>
          )}
        </section>

        {/* Features */}
        <section aria-label="Features" style={{ marginBottom: 32 }}>
          <h2 className="sr-only">What Clinicapt offers</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { icon: '\uD83D\uDCC5', title: '24/7 online booking', desc: 'Patients book while your receptionist sleeps' },
              { icon: '\uD83D\uDCAC', title: 'WhatsApp reminders', desc: 'Automated reminders that actually get read' },
              { icon: '\u2B50', title: 'Google review automation', desc: 'Turn happy patients into 5-star reviews' },
              { icon: '\uD83C\uDDFF\uD83C\uDDE6', title: 'Localised for SA', desc: 'HPCSA-compliant, local support, ZAR billing' },
            ].map((f, i) => (
              <div key={i} style={{
                background: '#f9f9f9', border: '0.5px solid #e0e0e0',
                borderRadius: 8, padding: '12px 14px'
              }}>
                <div style={{ fontSize: 16, marginBottom: 6 }} aria-hidden="true">{f.icon}</div>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 4 }}>{f.title}</h3>
                <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        <hr style={{ height: 0.5, border: 'none', background: '#ddd', marginBottom: 32 }} />

        {/* Testimonials */}
        <section aria-label="Testimonials">
          <h2 className="sr-only">What practitioners are saying</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              {
                quote: 'Our no-show rate dropped from 28% to under 9% in the first month. The WhatsApp reminders are the reason.',
                attr: 'Dr. T. Mabunda, GP, Soweto'
              },
              {
                quote: 'We went from 14 Google reviews to 91 in 8 weeks without asking a single patient ourselves.',
                attr: 'Dr. K. Van Wyk, Dentist, Menlyn'
              }
            ].map((t, i) => (
              <article key={i} style={{
                background: 'white', border: '0.5px solid #e0e0e0',
                borderRadius: 8, padding: '12px 14px'
              }}>
                <div style={{ color: '#D4A017', fontSize: 14, marginBottom: 6 }} aria-label="5 stars">{'\u2605\u2605\u2605\u2605\u2605'}</div>
                <blockquote style={{ fontSize: 13, fontStyle: 'italic', color: '#333', lineHeight: 1.5, marginBottom: 8, margin: '0 0 8px', padding: 0 }}>
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <p style={{ fontSize: 12, color: '#555', fontWeight: 600 }}>
                  — {t.attr}
                </p>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ textAlign: 'center', fontSize: 12, color: '#999', marginTop: 48 }}>
        <p>&copy; 2026 Clinicapt. All rights reserved.</p>
      </footer>

    </div>
  )
}

export default HomePage
